import React, { useState } from "react";
import { toast } from "react-toastify";
import api, { API_BASE } from "../../../utils/api";
import { useRestaurantConfig } from "../../../utils/restaurantConfig";
import { getCurrencySymbol } from "../../../helper/formatCurrency";
import { API_ENDPOINTS, VALIDATION } from "../constants";
import { getAppConfig } from "../../../utils/appConfig";
import { resolveProductPricing } from "../../../utils/productPricing";
import { getLocalizedName } from "../../../utils/localizedText";

// Smart search: typing "<code>+<qty>" or "<code>-<qty>" searches using just
// the code part, and (once that narrows to a single result) Enter applies
// that quantity — e.g. "0011+3" adds 3 of product 0011, "0011-2" removes 2.
// A bare code with no +/- suffix still just adds 1 on Enter.
const parseSmartSearch = (value) => {
  const plusIndex = value.indexOf("+");
  const minusIndex = value.indexOf("-");
  const opIndex = plusIndex !== -1 ? plusIndex : minusIndex;

  if (opIndex === -1) return { code: value, op: null, qty: 1 };

  const code = value.substring(0, opIndex);
  const op = value.substring(opIndex, opIndex + 1);
  const qtyStr = value.substring(opIndex + 1);
  const parsedQty = parseInt(qtyStr, 10);

  return {
    code,
    op,
    qty: qtyStr && !isNaN(parsedQty) ? parsedQty : 1,
  };
};

/**
 * Product Search Component
 * Search and select products to add to order with autocomplete dropdown
 * Searches when 2+ characters are entered
 *
 * Supports smart search quantity shortcuts — see parseSmartSearch above.
 *
 * @param {Object} props
 * @param {Function} props.onSelect - Callback function when product is selected
 * @param {Array} props.products - Current order lines, to look up an
 *   existing match for the "-" quantity shortcut
 * @param {Function} props.updateQuantity - Sets a product's quantity (by
 *   _id), for the "-" shortcut when some quantity remains
 * @param {Function} props.removeProduct - Removes a product (by _id) from
 *   the order, for the "-" shortcut when it would reach zero
 * @param {Function} props.onSearchFocus - Called when the search input gains
 *   focus (e.g. to collapse an accordion elsewhere on the page)
 * @param {Object} props.inputRef - Ref attached to the search input, so
 *   another field elsewhere on the page can focus it directly
 */
const ProductSearch = ({ onSelect, products = [], updateQuantity, removeProduct, onSearchFocus, inputRef }) => {
  const [options, setOptions] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [loading, setLoading] = useState(false);
  // Which result Up/Down is currently pointing at — this is a POS screen
  // driven mostly by keyboard, so arrow keys must be able to pick a result
  // without touching the mouse.
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const { locale } = useRestaurantConfig();
  const currencySymbol = getCurrencySymbol(locale);
  const { defaultImage } = getAppConfig();
  const [addOnPickerProduct, setAddOnPickerProduct] = useState(null);
  // Map of addOnId -> chosen quantity (0/absent = not selected).
  const [pickerQuantities, setPickerQuantities] = useState({});

  const visibleAddOns = (product) =>
    (product.addOns || []).filter((a) => a.isVisible !== false);

  const getImageSrc = (product) => {
    const path = product.image || product.imageUrl;

    if (path) {
      if (path.startsWith("http")) return path;
      return `${API_BASE}${path}`;
    }

    if (defaultImage) {
      return `${API_BASE}${defaultImage}`;
    }

    return "/img/finglider_logo.jpg";
  };
  const handleSearch = async (value) => {
    setInputValue(value);

    // Search only by the code part — a "+3"/"-2" quantity suffix isn't part
    // of the product name/code and must never reach the search API.
    const { code } = parseSmartSearch(value);

    if (code.length < VALIDATION.MIN_SEARCH_LENGTH) {
      setOptions([]);
      setShowDropdown(false);
      return;
    }

    setLoading(true);
    try {
      const res = await api.get(
        `${API_ENDPOINTS.SEARCH_PRODUCTS}?query=${code}`,
      );
      setOptions(res.data);
      setShowDropdown(res.data.length > 0);
      setHighlightedIndex(0);
    } catch (e) {
      console.log("Search error", e);
      setOptions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectProduct = (product, chosenAddOns = [], qty = 1) => {
    const { price, offerPrice, effectivePrice } = resolveProductPricing(product);
    const addOnsTotal = chosenAddOns.reduce(
      (s, a) => s + Number(a.price || 0) * Number(a.quantity || 1),
      0,
    );
    onSelect(
      {
        ...product,
        price,
        offerPrice,
        finalPrice: effectivePrice + addOnsTotal,
        imageUrl: getImageSrc(product),
        addOns: chosenAddOns,
        // Cache the product's full add-on catalog (product.addOns above gets
        // overwritten with just the chosen subset) so it can be edited later
        // without a re-fetch.
        availableAddOns: visibleAddOns(product),
      },
      qty,
    );
    setInputValue("");
    setOptions([]);
    setShowDropdown(false);
  };

  const handleProductClick = (product, qty = 1) => {
    if (visibleAddOns(product).length > 0) {
      setPickerQuantities({});
      setAddOnPickerProduct(product);
      // Close the search dropdown so it doesn't render on top of the picker.
      setInputValue("");
      setOptions([]);
      setShowDropdown(false);
      return;
    }
    handleSelectProduct(product, [], qty);
  };

  // Arrow keys move the highlighted result; Enter applies the "+N"/"-N"
  // quantity typed into the box (see parseSmartSearch) against whichever
  // result is highlighted — plain Enter with no suffix just adds 1.
  const handleSearchKeyDown = (e) => {
    if (e.key === "ArrowDown") {
      if (options.length === 0) return;
      e.preventDefault();
      setHighlightedIndex((i) => Math.min(i + 1, options.length - 1));
      return;
    }
    if (e.key === "ArrowUp") {
      if (options.length === 0) return;
      e.preventDefault();
      setHighlightedIndex((i) => Math.max(i - 1, 0));
      return;
    }
    if (e.key !== "Enter" || options.length === 0) return;
    e.preventDefault();

    const product = options[highlightedIndex] || options[0];
    const { op, qty } = parseSmartSearch(inputValue);

    if (op === "-") {
      const existing = products.find((p) => p._id === product._id);
      if (!existing) {
        toast.info(`${product.productName} isn't in the order`);
        return;
      }
      const remaining = existing.quantity - qty;
      if (remaining > 0) {
        updateQuantity?.(existing._id, remaining);
        toast.success(`${product.productName} quantity decreased!`);
      } else {
        removeProduct?.(existing._id);
      }
      setInputValue("");
      setOptions([]);
      setShowDropdown(false);
      return;
    }

    handleProductClick(product, qty);
  };

  const setPickerAddOnQty = (addOnId, qty) => {
    setPickerQuantities((prev) => {
      if (qty <= 0) {
        const next = { ...prev };
        delete next[addOnId];
        return next;
      }
      return { ...prev, [addOnId]: qty };
    });
  };

  const confirmAddOnPicker = () => {
    const chosen = visibleAddOns(addOnPickerProduct)
      .filter((a) => (pickerQuantities[a._id] || 0) > 0)
      .map((a) => ({
        addOnId: a._id,
        name: getLocalizedName(a.name),
        price: Number(a.price),
        quantity: pickerQuantities[a._id],
      }));
    handleSelectProduct(addOnPickerProduct, chosen);
    setAddOnPickerProduct(null);
  };

  return (
    <div className="relative">
      {/* Search Input */}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={(e) => handleSearch(e.target.value)}
          onFocus={() => {
            if (options.length > 0) setShowDropdown(true);
            onSearchFocus?.();
          }}
          onKeyDown={handleSearchKeyDown}
          placeholder="Search products by name..."
          className="w-full px-3 py-2 pl-9 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
        />
        <svg
          className="absolute left-2.5 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
          />
        </svg>
      </div>

      {/* Dropdown Results */}
      {showDropdown && (
        <>
          <div
            className="fixed inset-0 z-30"
            onClick={() => setShowDropdown(false)}
          />
          <div className="absolute z-40 w-full mt-2 bg-white border border-gray-200 rounded-lg shadow-lg max-h-96 overflow-y-auto">
            {loading ? (
              <div className="p-4 text-center text-gray-500">Loading...</div>
            ) : options.length === 0 ? (
              <div className="p-4 text-center text-gray-500">
                No products found
              </div>
            ) : (
              options.map((product, index) => {
                const { price, offerPrice } = resolveProductPricing(product);
                const isHighlighted = index === highlightedIndex;
                return (
                <div
                  key={product._id}
                  onClick={() => handleProductClick(product)}
                  onMouseEnter={() => setHighlightedIndex(index)}
                  className={`flex items-center gap-3 p-3 cursor-pointer border-b border-gray-100 last:border-b-0 ${
                    isHighlighted ? "bg-indigo-50" : "hover:bg-gray-50"
                  }`}
                >
                  {/* Product Image */}
                  <div className="w-12 h-12 flex-shrink-0">
                    <img
                      src={getImageSrc(product)}
                      alt={product.productName}
                      className="w-full h-full object-cover rounded"
                      onError={(e) => {
                        e.target.src = defaultImage
                          ? `${API_BASE}${defaultImage}`
                          : "/img/finglider_logo.jpg";
                      }}
                    />
                  </div>

                  {/* Product Info */}
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm text-gray-900 truncate">
                      {product.productName}
                    </div>
                    <div className="text-xs text-gray-500">
                      {product.categoryName || "Uncategorized"}
                    </div>
                    {visibleAddOns(product).length > 0 && (
                      <div className="text-xs text-indigo-600">Has add-ons</div>
                    )}
                  </div>

                  {/* Price */}
                  <div className="text-right">
                    {offerPrice > 0 ? (
                      <>
                        <div className="text-sm font-semibold text-green-600">
                          {currencySymbol}
                          {offerPrice}
                        </div>
                        <div className="text-xs text-gray-400 line-through">
                          {currencySymbol}
                          {price}
                        </div>
                      </>
                    ) : (
                      <div className="text-sm font-semibold text-gray-900">
                        {currencySymbol}
                        {price}
                      </div>
                    )}
                  </div>
                </div>
                );
              })
            )}
          </div>
        </>
      )}

      {/* Add-on Picker */}
      {addOnPickerProduct && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white rounded-xl shadow-xl w-[90%] max-w-[400px] mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-bold text-gray-900">
                Add-ons for {addOnPickerProduct.productName}
              </h3>
            </div>
            <div className="px-6 py-4 max-h-[300px] overflow-y-auto space-y-1">
              {visibleAddOns(addOnPickerProduct).map((addOn) => {
                const qty = pickerQuantities[addOn._id] || 0;
                return (
                  <div
                    key={addOn._id}
                    className="flex items-center justify-between py-2 border-b border-gray-100"
                  >
                    <div>
                      <div className="text-sm text-gray-800">{getLocalizedName(addOn.name)}</div>
                      <div className="text-xs text-gray-500">
                        {currencySymbol}
                        {Number(addOn.price).toFixed(2)} each
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => setPickerAddOnQty(addOn._id, qty - 1)}
                        disabled={qty === 0}
                        className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <svg className="w-3.5 h-3.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                        </svg>
                      </button>
                      <span className="text-sm font-semibold min-w-[16px] text-center">{qty}</span>
                      <button
                        type="button"
                        onClick={() => setPickerAddOnQty(addOn._id, qty + 1)}
                        className="w-7 h-7 flex items-center justify-center border border-gray-200 rounded hover:bg-gray-100"
                      >
                        <svg className="w-3.5 h-3.5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-2">
              <button
                onClick={() => setAddOnPickerProduct(null)}
                className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={confirmAddOnPicker}
                className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-lg shadow-md transition-colors"
              >
                Add Item
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProductSearch;
